import React from 'react';

import { useStores } from '@src/Common/Hooks/useStores';
import ButtonDefault from '@src/Components/Atoms/ButtonDefault/ButtonDefault';
import DropdownLocation from '@src/Components/Atoms/DropdownLocation/DropdownLocation';
import DropdownYear from '@src/Components/Atoms/DropdownYear/DropdownYear';
import { ModalCloseIcon } from '@src/Components/Icons/ModalCloseIcon/ModalCloseIcon';
import AnomalyCards from '../../Molecules/AnomalyCards/AnomalyCards';
import CheckboxEcosystem from '../../Molecules/CheckboxEcosystem/CheckboxEcosystem';
import DropdownCountry from '../../Molecules/DropdownCountry/DropdownCountry';
import DefaultModal from '../../Molecules/Modal/DefaultModal/DefaultModal';
import styles from './AdvanceFilter.module.scss';
import useAdvanceFilter from './useAdvanceFilter';

export interface IAdvanceFilter {
  show: boolean;
  onClose: () => void;
}

const AdvanceFilter: React.FC<IAdvanceFilter> = ({ show, onClose }) => {
  const {
    filters,
    showAnomalyCard,
    setShowAnomalyCard,
    onChangeSpecificCountry,
    onEcosystemChange,
    onStartYearChange,
    onEndYearChange,
    onLocationChange,
    onApply,
    minMaxYear,
    onAnomalyRatingChange
  } = useAdvanceFilter();

  const { AdvanceFilterStore } = useStores();

  return (
    <DefaultModal
      show={show}
      isBackgroundContainers={true}
      backgroundContent="transparent"
    >
      <section data-testid="advance-filter" className={styles['Container']}>
        <header className={styles['Header']}>
          <h4>Filter</h4>
          <button onClick={onClose}>
            <ModalCloseIcon />
          </button>
        </header>
        <main className={styles['Content']}>
          <div className={styles['YearContainer']}>
            <h1 className={styles['Title']}>Year</h1>
            <div className={styles['Year']}>
              <DropdownYear
                list={AdvanceFilterStore.allYears}
                defaultValue={filters.year.from ?? minMaxYear[0]}
                onChange={(v) => (v ? onStartYearChange(v) : null)}
              />
              <p>To</p>
              <DropdownYear
                list={AdvanceFilterStore.allYears.filter(
                  (v) => v >= (filters.year.from ?? 0)
                )}
                defaultValue={filters.year.end ?? minMaxYear[1]}
                onChange={(v) => (v ? onEndYearChange(v) : null)}
              />
            </div>
            <button
              className={styles['ShowAllYearButton']}
              onClick={() => {
                onStartYearChange(minMaxYear[0]);
                onEndYearChange(minMaxYear[1]);
              }}
            >
              Show all year
            </button>
          </div>
          <div className={styles['LocationContainer']}>
            <h1 className={styles['Title']}>Location</h1>
            <DropdownLocation
              defaultValue={filters.location !== 0 ? 'all' : 'unknown'}
              onChange={(v) => onLocationChange(v)}
            />
          </div>
          {filters.location !== 0 ? (
            <div className={styles['LocationContainer']}>
              <h1 className={styles['Title']}>Specific Country</h1>
              <DropdownCountry
                onChange={onChangeSpecificCountry}
                defaultValue={filters.contries?.filter((v) => v !== undefined)}
              />
            </div>
          ) : null}
          <div className={styles['EcosystemContainer']}>
            <CheckboxEcosystem
              defaultValue={filters.ecosystem?.filter((v) => v !== undefined)}
              onChange={onEcosystemChange}
            />
          </div>
          <div className={styles['AdvanceContainer']}>
            <ButtonDefault
              label="Advance Filter"
              customStyle="outline"
              variant="cyan"
              onClick={() => {
                setShowAnomalyCard((s) => !s);
              }}
            />
            {showAnomalyCard ? (
              <AnomalyCards
                defaultValue={filters.anomalyRating?.filter(
                  (v) => v !== undefined
                )}
                onChange={onAnomalyRatingChange}
              />
            ) : null}
          </div>
          <ButtonDefault variant="red" label="Apply Filter" onClick={onApply} />
        </main>
      </section>
    </DefaultModal>
  );
};

export default AdvanceFilter;
